/**
 * Currency Utilities
 * Single source of truth for all currency formatting in the frontend.
 *
 * CONTRACT: All displayed amounts must be in RUPEES. Cart and product APIs return
 * amounts in rupees. We never display paise. If an API ever returns paise by mistake,
 * use formatCurrencyFromPaisa so the display is still in rupees.
 */

/**
 * Format INR currency for display (amount in rupees)
 * @param {number} amount - Amount in rupees (e.g. 1500 or 1500.50)
 * @param {Object} options - Formatting options
 * @param {number} options.minimumFractionDigits - Minimum decimal places (default: 0)
 * @param {number} options.maximumFractionDigits - Maximum decimal places (default: 2)
 * @returns {string} Formatted currency string (e.g., "₹5,000" or "₹500.50")
 */
export const formatCurrency = (amount, options = {}) => {
  if (amount === undefined || amount === null || isNaN(Number(amount))) {
    return '₹0';
  }

  const {
    minimumFractionDigits = 0,
    maximumFractionDigits = 2,
  } = options;

  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(Number(amount));
};

/**
 * Format when amount is in paise (e.g. from an API that returns paise)
 * Use only when the API contract is paise; otherwise use formatCurrency(rupees).
 * @param {number} paise - Amount in paise (integer)
 * @returns {string} Formatted currency string
 */
export const formatCurrencyFromPaisa = (paisa) => {
  if (paisa === undefined || paisa === null || isNaN(Number(paisa))) {
    return '₹0';
  }
  const rupees = Number(paisa) / 100;
  return formatCurrency(rupees, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/**
 * Format currency with 2 decimal places (for detailed views like order details)
 * @param {number} amount - Amount in rupees
 * @returns {string} Formatted currency string with 2 decimal places
 */
export const formatCurrencyDetailed = (amount) => {
  return formatCurrency(amount, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/**
 * Format currency without decimal places (for summary views like dashboards)
 * @param {number} amount - Amount in rupees
 * @returns {string} Formatted currency string without decimals
 */
export const formatCurrencyRounded = (amount) => {
  return formatCurrency(amount, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};
